/**
 * Decoupled pub/sub for project-scoped RESOURCE invalidations, mirroring
 * `onUsageInvalidated` (usage-utilities.ts). The Workspace's presence socket
 * receives `{type:'resource_change', resource}` frames whenever another client
 * (or the server autonomously) mutates state this browser renders — team
 * membership, share links, deployments, env vars, git state, conversations —
 * and notifies here; any mounted panel that displays that resource subscribes
 * and refetches through its own loader. Producers and consumers never import
 * each other, and a panel that isn't mounted costs nothing.
 */

/** The invalidation keys the server emits (see api Collaboration ProjectResource). */
export type InvalidatedResource =
  | 'project'
  | 'team'
  | 'shares'
  | 'conversations'
  | 'deployments'
  | 'env'
  | 'git'
  | 'snapshots'
  | 'services'

const listenersByResource = new Map<InvalidatedResource, Set<() => void>>()

/**
 * Subscribe to invalidations of one resource.
 *
 * @param resource - The resource to watch.
 * @param listener - Called (with no payload — refetch, don't patch) each time
 *   the resource is invalidated.
 * @returns An unsubscribe function.
 */
export function onResourceInvalidated(
  resource: InvalidatedResource,
  listener: () => void,
): () => void {
  let set = listenersByResource.get(resource)
  if (!set) {
    set = new Set()
    listenersByResource.set(resource, set)
  }
  set.add(listener)
  return () => {
    set.delete(listener)
  }
}

/**
 * Notify every subscriber of one resource. Throw-isolated: one listener's
 * failure never blocks the rest (same contract as notifyUsageInvalidated).
 *
 * @param resource - The resource that changed.
 */
export function notifyResourceInvalidated(resource: InvalidatedResource): void {
  const set = listenersByResource.get(resource)
  if (!set) return
  for (const listener of [...set]) {
    try {
      listener()
    } catch (error) {
      console.error('Resource-invalidation listener failed', { resource, error })
    }
  }
}
